/* eslint-disable max-classes-per-file */
import React from 'react';
import moment from 'moment';

import viewIcon from '../../../assets/images/view.svg';
import editIcon from '../../../assets/images/ic_mode_edit_black_24px.svg';
import revokeIcon from '../../../assets/images/rotate.svg';
import { RenderTableActionButtons } from '../../commons/tableActionButton/tableActionButton';
import { VAS_ROLES } from './vasExpenseAuthUtils';

export const VAS_CHARGE_TYPES = {
  UNION: 'Union / Warai / Mathadi',
  MHE: 'Material Handling Equipment',
  DEDICATED_VEHICLE: 'Dedicated Vehicle',
  HANDLING_CHARGE: 'Handling Charge',
  CANTEEN_DELIVERY: 'Canteen Delivery',
  GOVERNMENT_COMPOUND_DELIVERY: 'Government Compound Delivery',
  PLANT_DELIVERY: 'Plant Delivery',
  MALL_DELIVERY: 'Mall Delivery',
  // T37678: raised automatically at delivery + approver-only — never a manual create option
  // (see SYSTEM_RAISED_CHARGE_TYPES). Listed here so the dashboard filter / grid / view show a label.
  HIGHER_FLOOR: 'Higher Floor Delivery'
};

// T37678: charge types raised by the system, not via the "Add VAS Charges" form. Single source of
// truth: system-raised implies not-creatable (filtered out of getAllowedChargeTypes), not
// user-editable/revocable (action-button gate), and carries higher-floor view detail.
export const SYSTEM_RAISED_CHARGE_TYPES = ['HIGHER_FLOOR'];

export const isSystemRaisedCharge = chargeType =>
  SYSTEM_RAISED_CHARGE_TYPES.includes(chargeType);

export const CHARGE_BASIS_LABELS = {
  RATE_PER_CN: 'Rate per CN',
  RATE_PER_BOX: 'Rate per Box',
  LUMPSUM_AMOUNT: 'Lumpsum Amount',
  RATE_PER_KG: 'Rate per KG'
};

class CountdownTimer extends React.Component {
  constructor(props) {
    super(props);
    this.state = { now: Date.now() };
  }

  componentDidMount() {
    this.interval = setInterval(() => {
      this.setState({ now: Date.now() });
    }, 60000);
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  render() {
    const { receiptDate } = this.props;
    if (!receiptDate) return null;

    const receiptTime = moment(receiptDate).valueOf();
    if (Number.isNaN(receiptTime)) return null;

    const expiresAt = receiptTime + 24 * 60 * 60 * 1000;
    const { now } = this.state;
    const diff = expiresAt - now;

    if (diff <= 0) {
      return null;
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return (
      <div className="vas-countdown-timer-style">{`${hours}h ${minutes}m`}</div>
    );
  }
}

export const ENTITY_TYPES = ['PRS', 'DRS', 'TRIP'];
export const ACTIVITY_TYPES = ['LOADING', 'UNLOADING'];
export const CHARGE_LOCATION_TYPES = {
  RIVIGO: 'Rivigo Location',
  CWH: 'Client Location'
};

// Charge types that require Activity Type field
export const ACTIVITY_TYPE_CHARGE_TYPES = ['UNION', 'HANDLING_CHARGE', 'MHE'];

// Charge type that requires Vehicle Type / Vehicle Number fields
export const VEHICLE_CHARGE_TYPE = 'DEDICATED_VEHICLE';

// Allowed Charge Basis per VasChargeType
export const getAllowedChargeBasis = chargeType => {
  if (chargeType === 'DEDICATED_VEHICLE') {
    return [
      { key: 'LUMPSUM_AMOUNT', val: 'Lumpsum Amount' },
      { key: 'RATE_PER_KG', val: 'Rate per KG' }
    ];
  }
  return [
    { key: 'RATE_PER_CN', val: 'Rate per CN' },
    { key: 'RATE_PER_BOX', val: 'Rate per Box' },
    { key: 'LUMPSUM_AMOUNT', val: 'Lumpsum Amount' },
    { key: 'RATE_PER_KG', val: 'Rate per KG' }
  ];
};

// Allowed Charge Types per EntityType
export const getAllowedChargeTypes = entityType => {
  if (entityType === 'PRS') {
    return ['UNION', 'MHE', 'DEDICATED_VEHICLE', 'HANDLING_CHARGE'].map(
      key => ({
        key,
        val: VAS_CHARGE_TYPES[key]
      })
    );
  }
  if (entityType === 'DRS') {
    return Object.entries(VAS_CHARGE_TYPES)
      .filter(([key]) => !isSystemRaisedCharge(key))
      .map(([key, val]) => ({ key, val }));
  }
  if (entityType === 'TRIP') {
    return ['MHE', 'HANDLING_CHARGE'].map(key => ({
      key,
      val: VAS_CHARGE_TYPES[key]
    }));
  }
  return [];
};

// Colors for request statuses
export const getStatusBadgeStyle = status => {
  switch (status) {
    case 'PENDING':
      return {
        backgroundColor: '#FEF3C7',
        color: '#D97706',
        padding: '4px 8px',
        borderRadius: '12px',
        fontWeight: 'bold'
      };
    case 'APPROVED':
      return {
        backgroundColor: '#D1FAE5',
        color: '#059669',
        padding: '4px 8px',
        borderRadius: '12px',
        fontWeight: 'bold'
      };
    case 'REJECTED':
      return {
        backgroundColor: '#FEE2E2',
        color: '#DC2626',
        padding: '4px 8px',
        borderRadius: '12px',
        fontWeight: 'bold'
      };
    case 'REVOKED':
      return {
        backgroundColor: '#F3F4F6',
        color: '#4B5563',
        padding: '4px 8px',
        borderRadius: '12px',
        fontWeight: 'bold'
      };
    default:
      return {};
  }
};

const getStatusClassName = status => {
  switch (status) {
    case 'PENDING':
      return 'vas-status status-pending';
    case 'APPROVED':
      return 'vas-status status-approved';
    case 'REJECTED':
      return 'vas-status status-rejected';
    case 'REVOKED':
      return 'vas-status status-revoked';
    default:
      return 'vas-status';
  }
};

class CnotesCell extends React.Component {
  constructor(props) {
    super(props);
    this.state = { isOpen: false, popupStyle: {} };
    this.containerRef = React.createRef();
    this.buttonRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    window.removeEventListener('scroll', this.closePopup, true);
  }

  closePopup = () => {
    this.setState({ isOpen: false });
    window.removeEventListener('scroll', this.closePopup, true);
  };

  handleClickOutside = event => {
    if (
      this.containerRef.current &&
      !this.containerRef.current.contains(event.target)
    ) {
      this.closePopup();
    }
  };

  toggleOpen = e => {
    e.stopPropagation();
    this.setState(prevState => {
      const opening = !prevState.isOpen;
      if (opening && this.buttonRef.current) {
        const rect = this.buttonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const dropDown = spaceBelow > 150;

        const style = {
          position: 'fixed',
          left: `${rect.left + rect.width / 2}px`,
          transform: 'translateX(-50%)',
          background: '#ffffff',
          color: '#1e293b',
          padding: '6px 12px',
          borderRadius: '4px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 99999,
          minWidth: '110px',
          maxHeight: '200px',
          overflowY: 'auto',
          border: '1px solid #cbd5e1'
        };

        if (dropDown) {
          style.top = `${rect.bottom + 8}px`;
        } else {
          style.bottom = `${window.innerHeight - rect.top + 8}px`;
        }

        window.addEventListener('scroll', this.closePopup, true);
        return { isOpen: true, popupStyle: style, dropDown };
      }
      window.removeEventListener('scroll', this.closePopup, true);
      return { isOpen: false };
    });
  };

  render() {
    const { cnotes } = this.props;
    if (!cnotes || !cnotes.length) return <span>-</span>;

    const { isOpen, popupStyle, dropDown } = this.state;

    return (
      <div
        ref={this.containerRef}
        style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center'
        }}
      >
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <span
          ref={this.buttonRef}
          onClick={this.toggleOpen}
          style={{
            cursor: 'pointer',
            padding: '3px 8px',
            borderRadius: '4px',
            background: '#ffffff',
            color: '#dd712f',
            fontWeight: '600',
            fontSize: '11px',
            border: '1px solid #dd712f',
            borderBottomWidth: '3px',
            borderBottomColor: '#b7531a',
            boxShadow: '0 1px 2px rgba(221, 113, 47, 0.15)',
            transition: 'all 0.1s ease',
            userSelect: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: 'translateY(0)'
          }}
          onMouseEnter={e => {
            const el = e.currentTarget;
            el.style.background = '#fff8f5';
            el.style.transform = 'translateY(-1.5px)';
            el.style.borderBottomWidth = '4.5px';
          }}
          onMouseLeave={e => {
            const el = e.currentTarget;
            el.style.background = '#ffffff';
            el.style.transform = 'translateY(0)';
            el.style.borderBottomWidth = '3px';
          }}
          onMouseDown={e => {
            const el = e.currentTarget;
            el.style.transform = 'translateY(1.5px)';
            el.style.borderBottomWidth = '1px';
          }}
          onMouseUp={e => {
            const el = e.currentTarget;
            el.style.transform = 'translateY(-1.5px)';
            el.style.borderBottomWidth = '4.5px';
          }}
          title="View CNs"
        >
          View CNs
        </span>
        {isOpen && (
          <div style={popupStyle}>
            {cnotes.map((cn, index) => (
              <div
                key={index}
                style={{
                  fontFamily: 'monospace',
                  fontSize: '11px',
                  padding: '3px 0',
                  textAlign: 'center',
                  color: '#334155',
                  fontWeight: '500'
                }}
              >
                {cn}
              </div>
            ))}
            <div
              style={{
                position: 'absolute',
                ...(dropDown ? { bottom: '100%' } : { top: '100%' }),
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '5px solid transparent',
                borderRight: '5px solid transparent',
                ...(dropDown
                  ? { borderBottom: '5px solid #ffffff' }
                  : { borderTop: '5px solid #ffffff' })
              }}
            />
            <div
              style={{
                position: 'absolute',
                ...(dropDown
                  ? {
                      bottom: '100%',
                      transform: 'translateX(-50%) translateY(-1px)'
                    }
                  : {
                      top: '100%',
                      transform: 'translateX(-50%) translateY(1px)'
                    }),
                left: '50%',
                width: 0,
                height: 0,
                borderLeft: '5px solid transparent',
                borderRight: '5px solid transparent',
                ...(dropDown
                  ? { borderBottom: '5px solid #cbd5e1' }
                  : { borderTop: '5px solid #cbd5e1' }),
                zIndex: -1
              }}
            />
          </div>
        )}
      </div>
    );
  }
}

export const getDisplayApprovedAmount = (item, role) => {
  if (!item || item.status !== 'APPROVED' || item.approvedAmount == null) {
    return null;
  }
  const reqAmt = Number(
    item.totalAmount != null
      ? item.totalAmount
      : item.chargedAmount != null
      ? item.chargedAmount
      : 0
  );
  const appAmt = Number(item.approvedAmount);

  if (role === VAS_ROLES.SAM_KAM || role === VAS_ROLES.MASTER_USER) {
    return appAmt;
  }

  if (reqAmt >= appAmt) {
    return appAmt;
  }
  return reqAmt;
};

export const getColumns = (role, actionsHandler) => {
  const columns = [
    {
      Header: 'Date / Raised By',
      accessor: 'receiptDate',
      minWidth: 120,
      Cell: row => {
        const date = row.value ? moment(row.value).format('MMM DD, YYYY') : '-';
        const raisedBy = row.original.raisedByName || '';
        return (
          <div>
            <div className="vas-cell-main">{date}</div>
            {raisedBy && <div className="vas-cell-sub">{raisedBy}</div>}
          </div>
        );
      }
    },
    {
      Header: 'Location / OU',
      accessor: 'chargeLocationType',
      minWidth: 120,
      Cell: row => {
        const { chargeLocationType, activityType } = row.original;

        // Extract directly from the root payload as addressDTOList is null in the list view
        const ouCode = row.original.ouCode || '';
        const clientName = row.original.clientName || '';

        const secondaryText = ouCode || clientName;

        return (
          <div>
            <div className="vas-cell-main vas-cell-bold">
              {`${chargeLocationType}${
                activityType ? ` - ${activityType}` : ''
              }`}
            </div>
            {secondaryText && (
              <div className="vas-cell-sub vas-cell-muted">{secondaryText}</div>
            )}
          </div>
        );
      }
    },
    {
      Header: 'Address',
      accessor: 'addressDTOList',
      minWidth: 160,
      Cell: row => {
        const { chargeLocationType, addressDTOList } = row.original;
        if (!addressDTOList || !addressDTOList.length) return <span>-</span>;
        const addr = addressDTOList[0];
        if (chargeLocationType === 'RIVIGO') {
          return <span className="vas-cell-main">{addr.ouCode || '-'}</span>;
        }
        return (
          <div>
            <div className="vas-cell-main">{addr.address || '-'}</div>
            {addr.pincode && (
              <div className="vas-cell-sub">{`Pin: ${addr.pincode}`}</div>
            )}
          </div>
        );
      }
    },
    {
      Header: 'Entity ID',
      accessor: 'entityId',
      minWidth: 100,
      Cell: row => {
        const { entityType, entityId } = row.original;
        return (
          <div>
            <div className="vas-cell-main">{entityId || '-'}</div>
            <div className="vas-cell-sub">{entityType || '-'}</div>
          </div>
        );
      }
    },
    {
      Header: 'CNs',
      accessor: 'cnoteCount',
      minWidth: 100,
      Cell: row => {
        const cnotes = row.original.applicableCnotes || [];
        return <CnotesCell cnotes={cnotes} />;
      }
    },
    {
      Header: 'Charge Type (Amt)',
      accessor: 'chargeType',
      minWidth: 180,
      Cell: row => {
        const label = VAS_CHARGE_TYPES[row.value] || row.value;
        const amount = row.original.totalAmount;
        return (
          <div>
            <div className="vas-cell-main">{label}</div>
            {amount !== null && amount !== undefined && (
              <div className="vas-cell-amount">
                {`\u20B9 ${Number(amount).toLocaleString('en-IN', {
                  minimumFractionDigits: 2
                })}`}
              </div>
            )}
          </div>
        );
      }
    },
    {
      Header: 'Approved Amount',
      accessor: 'approvedAmount',
      minWidth: 130,
      Cell: row => {
        const displayedApproved = getDisplayApprovedAmount(row.original, role);
        if (displayedApproved === null || displayedApproved === undefined) {
          return <span>-</span>;
        }
        return (
          <div className="vas-cell-amount vas-approved-amount vas-cell-bold">
            {`\u20B9 ${Number(displayedApproved).toLocaleString('en-IN', {
              minimumFractionDigits: 2
            })}`}
          </div>
        );
      }
    },
    {
      Header: 'Last Updated / Actioned By',
      accessor: 'lastActionedByName',
      minWidth: 175,
      Cell: row => {
        const name = row.value || '-';
        const updatedAt = row.original.lastUpdatedAt
          ? moment(row.original.lastUpdatedAt).format('MMM DD, YYYY')
          : '';
        return (
          <div>
            {updatedAt && (
              <div className="vas-cell-main" style={{ whiteSpace: 'nowrap' }}>
                {updatedAt}
              </div>
            )}
            <div className="vas-cell-sub" style={{ wordBreak: 'break-word' }}>
              {name}
            </div>
          </div>
        );
      }
    },
    {
      Header: 'Status',
      accessor: 'status',
      minWidth: 130,
      width: 130,
      className: 'sticky-status-col',
      headerClassName: 'sticky-status-col',
      Cell: row => (
        <div className="vas-status-cell-wrapper">
          <span className={getStatusClassName(row.value)}>
            {row.value
              ? row.value.charAt(0) + row.value.slice(1).toLowerCase()
              : '-'}
          </span>
          {row.value === 'PENDING' && (
            <CountdownTimer receiptDate={row.original.receiptDate} />
          )}
        </div>
      )
    },
    {
      Header: 'Actions',
      sortable: false,
      minWidth: 130,
      width: 130,
      className: 'sticky-actions-col',
      headerClassName: 'sticky-actions-col',
      Cell: row => {
        const item = row.original;
        const status = item.status || '';

        const buttonConfig = [];

        if (role === VAS_ROLES.SAM_KAM || role === VAS_ROLES.MASTER_USER) {
          const isApproveDisabled = status !== 'PENDING';
          buttonConfig.push({
            label: 'Approve',
            iconOnly: true,
            iconCb: () => (
              <svg viewBox="0 0 96 96" width="100%" height="100%">
                <path
                  fill={isApproveDisabled ? '#9CA3AF' : '#24AA38'}
                  d="M41.786 66L25 49.205l4.734-4.736 12.052 12.025L67.266 31 72 35.77z"
                />
              </svg>
            ),
            disabledFn: () => isApproveDisabled,
            onClick: () => actionsHandler.onApprove(item),
            style: { width: '40px', height: '40px', margin: '2%' }
          });

          const isRejectDisabled = status !== 'PENDING';
          buttonConfig.push({
            label: 'Reject',
            iconOnly: true,
            iconCb: () => (
              <svg viewBox="0 0 512 512" width="100%" height="100%">
                <path
                  fill={isRejectDisabled ? '#9CA3AF' : '#f44336'}
                  d="m350.273438 320.105469c8.339843 8.34375 8.339843 21.824219 0 30.167969-4.160157 4.160156-9.621094 6.25-15.085938 6.25-5.460938 0-10.921875-2.089844-15.082031-6.25l-64.105469-64.109376-64.105469 64.109376c-4.160156 4.160156-9.621093 6.25-15.082031 6.25-5.464844 0-10.925781-2.089844-15.085938-6.25-8.339843-8.34375-8.339843-21.824219 0-30.167969l64.109376-64.105469-64.109376-64.105469c-8.339843-8.34375-8.339843-21.824219 0-30.167969 8.34375-8.339843 21.824219-8.339843 30.167969 0l64.105469 64.109376 64.105469-64.109376c8.34375-8.339843 21.824219-8.339843 30.167969 0 8.339843 8.34375 8.339843 21.824219 0 30.167969l-64.109376 64.105469zm0 0"
                />
              </svg>
            ),
            disabledFn: () => isRejectDisabled,
            onClick: () => actionsHandler.onReject(item),
            style: { width: '40px', height: '40px', margin: '2%' }
          });
        }

        // System-raised charges (higher floor) expose only approve/reject — never edit or revoke.
        if (
          role === VAS_ROLES.GROUND_DEPS &&
          !isSystemRaisedCharge(item.chargeType)
        ) {
          const isEditDisabled = status !== 'PENDING';
          buttonConfig.push({
            label: 'Edit',
            iconOnly: true,
            iconRef: editIcon,
            disabledFn: () => isEditDisabled,
            onClick: () => actionsHandler.onEdit(item),
            style: { width: '40px', height: '40px', margin: '2%' }
          });

          const isRevokeDisabled = status !== 'PENDING';
          buttonConfig.push({
            label: 'Revoke',
            iconOnly: true,
            iconRef: revokeIcon,
            disabledFn: () => isRevokeDisabled,
            onClick: () => actionsHandler.onRevoke(item),
            style: { width: '40px', height: '40px', margin: '2%' }
          });
        }

        buttonConfig.push({
          label: 'view',
          iconOnly: true,
          iconRef: viewIcon,
          onClick: () => actionsHandler.onView(item),
          style: { width: '40px', height: '40px', margin: '2%' }
        });

        const priorityMap = {
          Edit: 1,
          Approve: 1,
          Revoke: 2,
          Reject: 2,
          view: 3
        };

        buttonConfig.sort(
          (a, b) => (priorityMap[a.label] || 99) - (priorityMap[b.label] || 99)
        );

        return (
          <div className="vas-claims-inline-353">
            <RenderTableActionButtons
              rowData={item}
              buttonConfig={buttonConfig}
            />
          </div>
        );
      }
    }
  ];

  if (role === VAS_ROLES.SAM_KAM || role === VAS_ROLES.MASTER_USER) {
    const samKamColumns = [
      {
        Header: 'Client (Code)',
        accessor: 'clientName',
        minWidth: 160,
        Cell: row => {
          const clientName = row.original.clientName || '-';
          const clientCode = row.original.clientCode || '-';
          return (
            <div>
              <div className="vas-cell-main vas-cell-bold">{clientCode}</div>
              <div className="vas-cell-sub vas-cell-muted">{clientName}</div>
            </div>
          );
        }
      },
      {
        Header: 'RP (Code)',
        accessor: 'rpName',
        minWidth: 160,
        Cell: row => {
          const rpName = row.original.rpName || '-';
          const rpCode = row.original.rpCode || '-';
          return (
            <div>
              <div className="vas-cell-main vas-cell-bold">{rpCode}</div>
              <div className="vas-cell-sub vas-cell-muted">{rpName}</div>
            </div>
          );
        }
      }
    ];
    columns.splice(5, 0, ...samKamColumns);
  }

  return columns;
};

export const isImageOrPdf = file => {
  if (!file) return false;
  const mimeType = file.type ? file.type.toLowerCase() : '';
  const fileName = file.name ? file.name.toLowerCase() : '';
  const validExtensions = [
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.bmp',
    '.webp',
    '.pdf'
  ];

  const hasValidExt = validExtensions.some(ext => fileName.endsWith(ext));
  if (!hasValidExt) return false;

  if (mimeType) {
    const isImageMime = mimeType.startsWith('image/');
    const isPdfMime = mimeType === 'application/pdf';
    if (!isImageMime && !isPdfMime) {
      return false;
    }
  }

  return true;
};
